import React, { useCallback, useEffect, useRef, useState } from "react";
import Typewriter from "typewriter-effect";

/** Ordered hero codec lines (intro + CV highlights). Casual tone; no em dashes for monospace clarity. */
export const HERO_CODEC_MESSAGES = [
  "Hi, I'm Awab Mohamed. People call me Nero.",
  "I'm a software engineer.",
  "I'm also a UI/UX designer.",
  "I've worked as a senior software engineer at UOB Bank.",
  "At UOB I pushed quality hard on Personal Internet Banking: Playwright and Applitools for E2E UI tests, plus kept Channel Admin sharp.",
  "I also grabbed Best Build at UOB's Gen AI Hackathon.",
  "Before UOB I was senior software engineer at Hong Leong Bank: Angular, Express, Keycloak, full stack internal portals.",
  "At Boost I shipped Boost Bills on the web: bill pay, prepaid top ups, merchant pin flows across Southeast Asia.",
  "At Tigerlab I led front end on insurance funnels: Angular and React talking to Django backends.",
  "At BixTech I built React and Material UI dashboards for a crypto exchange.",
  "I've got AWS Cloud Practitioner in the bag.",
  "Software Engineering honours from University of Malaya. #1 in Malaysia, strong globally.",
  "High school I finished top three in my region with a 99.75% score.",
  "I built Invisage: AI analytics that turns messy uploads into dashboards people actually ship.",
  "Side gigs like UI Coach, Copy Page, Makers League. I like ideas that make it to production.",
];

export default function HeroIntroMessages({ prefersReducedMotion, booted }) {
  const threadRef = useRef(null);
  const [completed, setCompleted] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const onLineDone = useCallback(() => {
    setCompleted((prev) => [
      ...prev,
      HERO_CODEC_MESSAGES[prev.length],
    ]);
    setActiveIndex((i) => i + 1);
  }, []);

  /** Typewriter mutates the DOM per character without React renders; observers keep the viewport pinned to the cursor. */
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return undefined;

    const snapToBottom = () => {
      el.scrollTop = el.scrollHeight;
    };

    const snapSoon = () => {
      requestAnimationFrame(snapToBottom);
    };

    snapToBottom();

    const mutationObserver = new MutationObserver(snapSoon);
    mutationObserver.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const resizeObserver = new ResizeObserver(snapSoon);
    resizeObserver.observe(el);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  if (prefersReducedMotion) {
    return (
      <>
        <p className="sr-only">
          {HERO_CODEC_MESSAGES.join(" ")}
        </p>
        <div
          className="codec-shell__thread"
          ref={threadRef}
          aria-hidden="true"
        >
          {HERO_CODEC_MESSAGES.map((text, i) => (
            <p key={i} className="codec-shell__bubble-line">
              {text}
            </p>
          ))}
        </div>
      </>
    );
  }

  const stillTyping = activeIndex < HERO_CODEC_MESSAGES.length && booted;

  return (
    <>
      <p className="sr-only" aria-live="polite">
        {completed.join(" ")}
        {stillTyping ? " … typing." : ""}
      </p>
      <div
        className="codec-shell__thread"
        ref={threadRef}
        aria-hidden="true"
      >
        {completed.map((text, i) => (
          <p key={`done-${i}`} className="codec-shell__bubble-line">
            {text}
          </p>
        ))}
        {stillTyping ? (
          <p
            key={activeIndex}
            className="codec-shell__bubble-line codec-shell__bubble-line--typing"
          >
            <Typewriter
              onInit={(tw) => {
                tw.pauseFor(activeIndex === 0 ? 650 : 280)
                  .changeDelay(42)
                  .typeString(HERO_CODEC_MESSAGES[activeIndex])
                  .callFunction(onLineDone)
                  .start();
              }}
              options={{
                loop: false,
                cursor: "|",
              }}
            />
          </p>
        ) : null}
      </div>
    </>
  );
}
